import { useState, useEffect } from "react";
import { Menu, X, Phone, Mail, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { prefetchPage } from "@/App";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    if (!isHomePage) {
      navigate("/");
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  const navLinks = [
    { label: "Home", to: "/", isLink: true },
    { label: "About", to: "/about", isLink: true },
    { label: "Products", to: "/products", isLink: true },
    
    { label: "Gallery", to: "/gallery", isLink: true },
    { label: "Blog", to: "/blog", isLink: true },
    { label: "FAQ", to: "/faq", isLink: true },
    
  ];

  return (
    <>
      {/* Top utility bar */}
      <div className="bg-primary text-primary-foreground text-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-10">
          <div className="flex items-center gap-6">
            <a href="tel:+917419900071" className="flex items-center gap-1.5 hover:opacity-80 transition-opacity font-body">
              <Phone className="h-3.5 w-3.5" />
              +91 74199 00071
            </a>
            <a href="mailto:info@sandhufeeds.com" className="flex items-center gap-1.5 hover:opacity-80 transition-opacity font-body">
              <Mail className="h-3.5 w-3.5" />
              info@sandhufeeds.com
            </a>
          </div>
          <span className="font-body opacity-80">Jind Road, Assandh, Haryana 132039</span>
        </div>
      </div>

      {/* Main navigation */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? "bg-background/95 backdrop-blur-md shadow-sm border-b border-border" 
          : "bg-background border-b border-border/50"
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center gap-3">
              <img
                src="/lovable-uploads/e96b7e90-3233-4052-88c0-b565f9c3ca04.png"
                alt="Sandhu Feeds"
                className="h-14 w-auto"
              />
              <div className="flex flex-col">
                <h1 className="text-xl font-bold text-primary font-display">Sandhu Feeds</h1>
                <p className="text-xs text-muted-foreground font-body tracking-wider uppercase">Premium Poultry Nutrition</p>
              </div>
            </Link>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  onMouseEnter={() => prefetchPage(link.to)}
                  className={`px-4 py-2 text-sm font-medium font-body transition-colors rounded-md ${
                    location.pathname === link.to
                      ? "text-primary bg-primary/5"
                      : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={() => scrollToSection("appointment")}
                className="px-4 py-2 text-sm font-medium font-body text-foreground/70 hover:text-primary hover:bg-primary/5 transition-colors rounded-md"
              >
                Contact
              </button>
            </div>

            {/* CTA */}
            <div className="hidden md:block">
              <Button
                onClick={() => scrollToSection("appointment")}
                className="btn-copper text-sm"
                size="sm"
              >
                Get a Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Mobile hamburger */}
            <div className="md:hidden">
              <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)}>
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div className="md:hidden bg-background border-t border-border">
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-3 text-foreground/80 hover:text-primary hover:bg-primary/5 rounded-md font-body"
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={() => scrollToSection("appointment")}
                className="block w-full text-left px-4 py-3 text-foreground/80 hover:text-primary hover:bg-primary/5 rounded-md font-body"
              >
                Contact
              </button>
              <div className="pt-3 px-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground font-body">
                  <Phone className="h-3.5 w-3.5" /> +91 74199 00071
                </div>
                <a href="mailto:info@sandhufeeds.com" className="flex items-center gap-2 text-sm text-muted-foreground font-body mt-2 hover:opacity-80 transition-opacity">
                  <Mail className="h-3.5 w-3.5" /> info@sandhufeeds.com
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navigation;
